import { FC, useState, FormEvent, ChangeEvent } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { breadcrumbSchema } from '../../utils/schemas';
import Turnstile from '../../components/Turnstile/Turnstile';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

interface ApplyFormData {
  certificationStandards: string[];
  requestType: string;
  companyName: string;
  vatNumber: string;
  streetAddress: string;
  city: string;
  stateProvince: string;
  zipCode: string;
  country: string;
  phone: string;
  email: string;
  sector: string;
  totalPersonnel: string;
  externalActivities: string;
  website: string; // honeypot - left blank by real users
  acceptTerms: boolean;
  acceptPrivacy: boolean;
}

interface FormStatus {
  submitting: boolean;
  submitted: boolean;
  success: boolean;
  message: string;
}

const ApplyOnline: FC = () => {
  const { t, i18n } = useTranslation();
  const isAlbanian = i18n.language?.startsWith('sq');

  const isoOptions = [
    { value: 'ISO 9001:2015', label: 'ISO 9001:2015' },
    { value: 'ISO 14001:2015', label: 'ISO 14001:2015' },
    { value: 'ISO 22000:2018', label: 'ISO 22000:2018' },
    { value: 'ISO 22301:2019', label: 'ISO 22301:2019' },
    { value: 'ISO 27001:2022', label: 'ISO 27001:2022' },
    { value: 'ISO 37001:2016', label: 'ISO 37001:2016' },
    { value: 'ISO 39001:2012', label: 'ISO 39001:2012' },
    { value: 'ISO 45001:2018', label: 'ISO 45001:2018' },
    { value: 'ISO 50001:2018', label: 'ISO 50001:2018' },
    { value: 'HACCP', label: 'HACCP – Codex Alimentarius' },
    { value: 'CE Marking', label: t('applyOnline.form.ceMarking') },
  ];

  const [formData, setFormData] = useState<ApplyFormData>({
    certificationStandards: [],
    requestType: '',
    companyName: '',
    vatNumber: '',
    streetAddress: '',
    city: '',
    stateProvince: '',
    zipCode: '',
    country: '',
    phone: '',
    email: '',
    sector: '',
    totalPersonnel: '',
    externalActivities: '',
    website: '',
    acceptTerms: false,
    acceptPrivacy: false
  });

  const [status, setStatus] = useState<FormStatus>({
    submitting: false,
    submitted: false,
    success: false,
    message: ''
  });

  const [turnstileToken, setTurnstileToken] = useState('');

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleStandardChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      certificationStandards: prev.certificationStandards.includes(value)
        ? prev.certificationStandards.filter(s => s !== value)
        : [...prev.certificationStandards, value]
    }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus({ submitting: true, submitted: false, success: false, message: '' });

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
      const response = await fetch(`${apiBaseUrl}/forms/apply-online/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          certificationStandard: formData.certificationStandards.join(', '),
          turnstile_token: turnstileToken,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus({
          submitting: false,
          submitted: true,
          success: true,
          message: t('applyOnline.form.successMessage')
        });
        setFormData({
          certificationStandards: [],
          requestType: '',
          companyName: '',
          vatNumber: '',
          streetAddress: '',
          city: '',
          stateProvince: '',
          zipCode: '',
          country: '',
          phone: '',
          email: '',
          sector: '',
          totalPersonnel: '',
          externalActivities: '',
          website: '',
          acceptTerms: false,
          acceptPrivacy: false
        });
        setTurnstileToken('');
      } else {
        setStatus({
          submitting: false,
          submitted: true,
          success: false,
          message: data.message || t('applyOnline.form.errorMessage')
        });
      }
    } catch {
      setStatus({
        submitting: false,
        submitted: true,
        success: false,
        message: t('applyOnline.form.errorMessage')
      });
    }
  };

  return (
    <div className="apply-online-page">
      <Helmet>
        <title>{t('applyOnline.meta.title')}</title>
        <meta name="description" content={t('applyOnline.meta.description')} />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Apply Online', path: '/apply-online' },
          ]))}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <h1>{t('applyOnline.hero.title')}</h1>
          <p className="contact-subtitle">{t('applyOnline.hero.subtitle')}</p>
        </div>
      </section>

      {/* Form Section */}
      <section className="section section-white">
        <div className="container">
          <div className="apply-form-container">

            {status.submitted && (
              <div className={`form-message ${status.success ? 'form-message-success' : 'form-message-error'}`}>
                {status.message}
              </div>
            )}

            <form className="apply-form" onSubmit={handleSubmit}>
              <div className="hp-field" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {/* Section 1: Certification Standard */}
              <div className="form-section">
                <h2>{t('applyOnline.form.sections.certification')}</h2>
                <div className="form-grid">
                  <div className="form-group form-group-full">
                    <label>{t('applyOnline.form.certificationStandard')} *</label>
                    <div className="checkbox-group">
                      {isoOptions.map(opt => (
                        <label key={opt.value} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={formData.certificationStandards.includes(opt.value)}
                            onChange={() => handleStandardChange(opt.value)}
                            disabled={status.submitting}
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group form-group-full">
                    <label>{t('applyOnline.form.requestType')} *</label>
                    <div className="radio-group">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="requestType"
                          value="initial"
                          checked={formData.requestType === 'initial'}
                          onChange={handleChange}
                          required
                          disabled={status.submitting}
                        />
                        <span>{t('applyOnline.form.requestTypes.initial')}</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="requestType"
                          value="renovation"
                          checked={formData.requestType === 'renovation'}
                          onChange={handleChange}
                          disabled={status.submitting}
                        />
                        <span>{t('applyOnline.form.requestTypes.renovation')}</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="requestType"
                          value="transfer"
                          checked={formData.requestType === 'transfer'}
                          onChange={handleChange}
                          disabled={status.submitting}
                        />
                        <span>{t('applyOnline.form.requestTypes.transfer')}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Company Data */}
              <div className="form-section">
                <h2>{t('applyOnline.form.sections.companyData')}</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="companyName">{t('applyOnline.form.companyName')} *</label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="vatNumber">{t('applyOnline.form.vatNumber')} *</label>
                    <input
                      type="text"
                      id="vatNumber"
                      name="vatNumber"
                      value={formData.vatNumber}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Address */}
              <div className="form-section">
                <h2>{t('applyOnline.form.sections.address')}</h2>
                <div className="form-grid">
                  <div className="form-group form-group-full">
                    <label htmlFor="streetAddress">{t('applyOnline.form.streetAddress')} *</label>
                    <input
                      type="text"
                      id="streetAddress"
                      name="streetAddress"
                      value={formData.streetAddress}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="city">{t('applyOnline.form.city')} *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </div>
                  {!isAlbanian && (
                  <div className="form-group">
                    <label htmlFor="stateProvince">{t('applyOnline.form.stateProvince')} *</label>
                    <input
                      type="text"
                      id="stateProvince"
                      name="stateProvince"
                      value={formData.stateProvince}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="zipCode">{t('applyOnline.form.zipCode')} *</label>
                    <input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="country">{t('applyOnline.form.country')} *</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </div>
                </div>
              </div>

              {/* Section 4: Contact Info */}
              <div className="form-section">
                <h2>{t('applyOnline.form.sections.contact')}</h2>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="phone">{t('applyOnline.form.phone')} *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">{t('applyOnline.form.email')} *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                  </div>
                </div>
              </div>

              {/* Section 5: Business Information */}
              <div className="form-section">
                <h2>{t('applyOnline.form.sections.business')}</h2>
                <div className="form-grid">
                  <div className="form-group form-group-full">
                    <label htmlFor="sector">{t('applyOnline.form.sector')} *</label>
                    <input
                      type="text"
                      id="sector"
                      name="sector"
                      value={formData.sector}
                      onChange={handleChange}
                      placeholder={t('applyOnline.form.sectorPlaceholder')}
                      required
                      disabled={status.submitting}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="totalPersonnel">{t('applyOnline.form.ownersManagers')} * – {t('applyOnline.form.totalPersonnel')}</label>
                    <input
                      type="number"
                      id="totalPersonnel"
                      name="totalPersonnel"
                      value={formData.totalPersonnel}
                      onChange={handleChange}
                      min="0"
                      required
                      disabled={status.submitting}
                    />
                  </div>

                  <div className="form-group form-group-full">
                    <label htmlFor="externalActivities">{t('applyOnline.form.externalActivities')}</label>
                    <textarea
                      id="externalActivities"
                      name="externalActivities"
                      rows={4}
                      value={formData.externalActivities}
                      onChange={handleChange}
                      placeholder={t('applyOnline.form.externalActivitiesPlaceholder')}
                      disabled={status.submitting}
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Terms & Privacy Consent */}
              <div className="form-section">
                <div className="form-group form-group-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                    <span dangerouslySetInnerHTML={{ __html: t('applyOnline.form.acceptTerms') }} />
                  </label>
                </div>
                <div className="form-group form-group-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="acceptPrivacy"
                      checked={formData.acceptPrivacy}
                      onChange={handleChange}
                      required
                      disabled={status.submitting}
                    />
                    <span dangerouslySetInnerHTML={{ __html: t('applyOnline.form.acceptPrivacy') }} />
                  </label>
                </div>
              </div>

              {TURNSTILE_SITE_KEY && (
                <div className="form-section">
                  <Turnstile siteKey={TURNSTILE_SITE_KEY} onVerify={setTurnstileToken} onExpire={() => setTurnstileToken('')} />
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary-large apply-submit-btn"
                disabled={status.submitting || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
              >
                {status.submitting ? t('applyOnline.form.submitting') : t('applyOnline.form.submit')}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ApplyOnline;
